
/**
 * Module dependencies.
 */

import { BackgroundImage, Icon, Image } from 'react-components/media';
import { Button, Heading, ImageSources, Link, Text, logo } from '../design-system';
import { Helmet } from 'react-helmet';
import { Trans } from 'react-i18next';
import { color, media } from 'react-components/styles';
import { useTranslate } from '../i18n';
import React from 'react';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  backgroundImage: ImageSources;
  backgroundOverlay: ImageSources;
  earlyAccessLink: string;
  imageMobile: ImageSources;
  logoLink: string;
  supportEmail: string;
};

/**
 * `Section` styled component.
 */

const Section = styled.section`
  ${media.min('md')`
    column-gap: 32px;
    display: grid;
    grid-template-columns: repeat(12, 1fr);
  `}
`;

/**
 * `Header` styled component.
 */

const Header = styled.header`
  display: none;
  grid-column: 1 / span 6;
  position: relative;

  ${media.min('md')`
    display: block;
  `}
`;

/**
 * `StyledBackgroundImage` styled component.
 */

const StyledBackgroundImage = styled(BackgroundImage)`
  background-size: contain;
`;

/**
 * `IconWrapper` styled component.
 */

const IconWrapper = styled.div`
  left: 24px;
  position: absolute;
  top: 32px;
`;

/**
 * `Main` styled component.
 */

const Main = styled.main`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow-y: auto;

  ${media.min('md')`
    grid-column: span 6 / -1;
    padding-right: 32px;
  `}
`;

/**
 * `ContentWrapper` styled component.
 */

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: 0 auto;
  max-width: 343px;
  padding: 0 16px;
  width: 100%;

  ${media.min('ms')`
    max-width: 410px;
  `}

  ${media.min('md')`
    max-width: 448px;
    padding: 0;
  `}
`;

/**
 * `Content` styled component.
 */

const Content = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
`;

/**
 * `StyledLink` styled component.
 */

const StyledLink = styled(Link)`
  margin: 32px 0 16px 16px;
  width: max-content;

  ${media.min('md')`
    display: none;
  `}
`;

/**
 * `StyledImage` styled component.
 */

const StyledImage = styled(Image)`
  ${media.min('md')`
    display: none;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Heading).attrs({ as: 'h1' })`
  color: ${color('primary')};
  font-weight: 600;
  padding-top: 32px;

  ${media.min('md')`
    padding-top: 0;
  `}
`;

/**
 * `Lead` styled component.
 */

const Lead = styled(Text).attrs({ size: 'large' })`
  padding-top: 24px;

  ${media.min('md')`
    padding-top: 50px;
  `}
`;

/**
 * `Support` styled component.
 */

const Support = styled(Text).attrs({ size: 'large' })`
  color: ${color('gray500')};
  padding-top: 8px;
`;

/**
 * `ActionWrapper` styled component.
 */

const ActionWrapper = styled.div`
  padding: 24px 0 56px;

  ${media.min('md')`
    padding-bottom: 72px;
  `}
`;

/**
 * `CreatePayspaceTitle` styled component.
 */

const CreatePayspaceTitle = styled(Text).attrs({ size: 'large' })`
  color: ${color('gray500')};
  margin-bottom: 12px;
  text-align: center;
`;

/**
 * Export `PayspaceNotFound` component.
 */

export function PayspaceNotFound(props: Props): JSX.Element {
  const { backgroundImage, backgroundOverlay, earlyAccessLink, imageMobile, logoLink, supportEmail } = props;
  const { translate } = useTranslate();

  return (
    <>
      <Helmet>
        <title>
          {translate('payspaceNotFound.metaTitle')}
        </title>
      </Helmet>

      <Section>
        <Header>
          <BackgroundImage {...backgroundOverlay} />

          <StyledBackgroundImage {...backgroundImage} />

          <IconWrapper>
            <Link
              href={logoLink}
              rel={'noopener'}
              target={'_blank'}
            >
              <Icon
                aria-hidden
                color={color('white')}
                icon={logo}
                size={'100px'}
              />
            </Link>
          </IconWrapper>
        </Header>

        <Main>
          <StyledLink
            href={logoLink}
            rel={'noopener'}
            target={'_blank'}
          >
            <Icon
              aria-hidden
              color={color('primary')}
              icon={logo}
              size={'100px'}
            />
          </StyledLink>

          <ContentWrapper>
            <Content>
              <StyledImage {...imageMobile} />

              <Title>
                {translate('payspaceNotFound.title')}
              </Title>

              <Lead>
                {translate('payspaceNotFound.lead')}
              </Lead>

              <Support>
                <Trans
                  components={[
                    <Link
                      href={`mailto:${supportEmail}`}
                      key={0}
                      rel={'noopener'}
                      target={'_blank'}
                    >
                      {supportEmail}
                    </Link>
                  ]}
                  i18nKey={'payspaceNotFound.support'}
                  ns={'common'}
                />
              </Support>
            </Content>

            <ActionWrapper>
              <CreatePayspaceTitle>
                {translate('payspaceNotFound.createPayspaceTitle')}
              </CreatePayspaceTitle>

              <Button
                href={earlyAccessLink}
                size={'large'}
              >
                {translate('payspaceNotFound.createPayspaceButton')}
              </Button>
            </ActionWrapper>
          </ContentWrapper>
        </Main>
      </Section>
    </>
  );
}
